import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SupportService } from "../services/support.service";
import { ConversationThread } from "../dto/index.dto";
import { DEFAULT_LIMIT } from "../../../shared/dto/pagination.dto";

export const useGetConversationsHook = (
  page: number,
  limit: number = 20
) => {
  return useQuery({
    queryKey: ["support", "conversations", page, limit],
    queryFn: () => SupportService.getConversations(page, limit),
    placeholderData: keepPreviousData,
  });
};

export const useGetConversationThreadHook = (userId: string) => {
  return useQuery({
    queryKey: ["support", "thread", userId],
    queryFn: () => SupportService.getConversationThread(userId),
    enabled: !!userId,
  });
};

export const useSendMessageHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, content }: { userId: string; content: string }) =>
      SupportService.sendMessage(userId, content),
    onSuccess: (message, { userId }) => {
      // The message was just sent through this admin composer, so it is
      // always from the admin — force this locally rather than trust the
      // POST response's isFromAdmin field (the GET thread endpoint gets it
      // right, but the create response doesn't, which was flipping the
      // bubble side until the next refetch).
      queryClient.setQueryData<ConversationThread | undefined>(
        ["support", "thread", userId],
        (old) =>
          old
            ? {
                ...old,
                messages: [...old.messages, { ...message, isFromAdmin: true }],
              }
            : old
      );
      queryClient.invalidateQueries({
        queryKey: ["support", "conversations"],
      });
    },
  });
};

export const useGetFeedbacksHook = (
  page: number,
  limit: number = DEFAULT_LIMIT
) => {
  return useQuery({
    queryKey: ["support", "feedbacks", page, limit],
    queryFn: () => SupportService.getFeedbacks(page, limit),
    placeholderData: keepPreviousData,
  });
};

export const useGetAboutHook = () => {
  return useQuery({
    queryKey: ["support", "about"],
    queryFn: () => SupportService.getAbout(),
  });
};

export const useUpdateAboutHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => SupportService.updateAbout(content),
    onSuccess: (updated) => {
      queryClient.setQueryData(["support", "about"], updated);
    },
  });
};
